import React, { useState, useRef, useEffect } from 'react'; 
import { patientApi } from '../services/api';
import { storeFormData, getStoredFormData } from '../utils/sessionStorage';

/**
 * InsuranceVerification component for collecting and managing insurance information
 *
 * @param {Object} props - Component props
 * @param {Object} props.tokenData - Patient token data
 * @param {Function} props.onSuccess - Callback function to call when verification is successful
 * @returns {JSX.Element} The insurance verification component
 */
const InsuranceVerification = ({ tokenData, onSuccess }) => {
  // State for the insurance packages list
  const [insurancePackages, setInsurancePackages] = useState([]);
  const [isLoadingPackages, setIsLoadingPackages] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  
  // Array of loading messages to rotate through
  const loadingMessages = [
    "Loading insurance information...",
    "Still trying to get your insurance data...",
    "This is taking longer than expected...",
    "Let's try this a different way. Hold on...",
    "Seems like we ran into some issue here... [SKIP_LINK]" // Add a marker for the skip link
  ];
  
  // State for the form to add/edit an insurance package
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPackageId, setCurrentPackageId] = useState(null);
  const [insuranceProvider, setInsuranceProvider] = useState('');
  const [memberId, setMemberId] = useState('');
  const [groupNumber, setGroupNumber] = useState('');
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [frontPreview, setFrontPreview] = useState('');
  const [backPreview, setBackPreview] = useState('');
  
  // State for the insurance verification flow
  const [hasInsuranceChanged, setHasInsuranceChanged] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [formId] = useState('insurance_verification');
  
  // State for delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState(null);
  
  const frontInputRef = useRef(null);
  const backInputRef = useRef(null);
  
  // Fetch insurance packages from the API
  const fetchInsurancePackages = async () => {
    console.log('fetchInsurancePackages: Function called');
    try {
      setIsLoadingPackages(true);
      setErrorMessage(''); // Clear previous errors
      
      console.log('fetchInsurancePackages: Calling patientApi.getPatientInsurancePackages with sub:', tokenData.sub);
      const packages = await patientApi.getPatientInsurancePackages(tokenData.sub, tokenData); // Use tokenData.sub
      console.log('fetchInsurancePackages: API call returned', packages);
      setInsurancePackages(packages);
      
      // If there are no packages, show the form to add a new one
      if (packages.length === 0) {
        setIsAddingNew(true);
      }
      
      // Set loading to false after successful fetch
      setIsLoadingPackages(false);
    } catch (error) {
      console.error('Error fetching insurance packages:', error);
      
      // Extract error message from API response if available
      let msg = "We ran into an error while retrieving your insurance information.";
      let shouldShowAddNew = false;
      
      if (error.response) {
        // Handle specific status codes
        if (error.response.status === 404) {
          // 404 means no insurance found for this patient - this is expected for new patients
          msg = "No insurance information found. Please add your insurance details.";
          shouldShowAddNew = true;
        } else if (error.response?.data?.message) {
          msg = error.response.data.message;
        }
      } else if (error.message) {
        msg = error.message;
      }
      
      setErrorMessage(msg);
      setIsLoadingPackages(false); // Stop loading on error
      
      // If it's a 404 (no insurance found), show the form to add a new one
      if (shouldShowAddNew) {
        setIsAddingNew(true);
      }
      
      // Log the error for debugging
      console.error('Error details:', error.response?.data || error.message || error);
    }
  }; // Removed dependency array for fetchInsurancePackages

  // Load insurance packages when component mounts or tokenData changes
  useEffect(() => {
    console.log('InsuranceVerification useEffect triggered'); // New log here
    console.log('InsuranceVerification useEffect: tokenData changed', tokenData);
    if (tokenData && tokenData.sub) { // Check for tokenData.sub
      console.log('InsuranceVerification useEffect: tokenData and sub available, calling fetchInsurancePackages');
      fetchInsurancePackages();
    } else {
      console.log('InsuranceVerification useEffect: tokenData or sub not available');
    }
  }, [tokenData]); // Depend only on tokenData
  
  // Rotate through loading messages
  useEffect(() => {
    let messageTimer;
    
    if (isLoadingPackages) {
      messageTimer = setInterval(() => {
        setLoadingMessageIndex(prevIndex => {
          const nextIndex = (prevIndex + 1) % loadingMessages.length;
          // Stop rotating if we reach the last message
          if (nextIndex === loadingMessages.length - 1) {
            clearInterval(messageTimer);
          }
          return nextIndex;
        });
      }, 5000); // Change message every 5 seconds
    }
    
    return () => {
      clearInterval(messageTimer);
    };
  }, [isLoadingPackages, loadingMessages.length]);
  
  // Load saved form data when component mounts
  useEffect(() => {
    if (tokenData && tokenData.patient_id && isAddingNew) {
      const storedData = getStoredFormData(formId, tokenData.patient_id);
      if (storedData) {
        console.log('Loading saved insurance data:', storedData);
        
        // Set form fields from stored data
        if (storedData.insuranceProvider) {
          setInsuranceProvider(storedData.insuranceProvider);
        }
        
        if (storedData.memberId) {
          setMemberId(storedData.memberId);
        }
        
        if (storedData.groupNumber) {
          setGroupNumber(storedData.groupNumber);
        }
        
        // Load image previews if available
        if (storedData.frontPreview) {
          setFrontPreview(storedData.frontPreview);
        }
        
        if (storedData.backPreview) {
          setBackPreview(storedData.backPreview);
        }
      }
    }
  }, [tokenData, formId, isAddingNew]);
  
  // Save form data whenever it changes
  useEffect(() => {
    if (tokenData && tokenData.patient_id && (isAddingNew || isEditing)) {
      // Only save if at least one field has data
      if (insuranceProvider || memberId || groupNumber || frontPreview || backPreview) {
        const formData = {
          insuranceProvider,
          memberId,
          groupNumber,
          frontPreview,
          backPreview
        };
        
        // Use debounce to avoid too many saves
        const timeoutId = setTimeout(() => {
          storeFormData(formId, tokenData.patient_id, formData);
          console.log('Saved insurance data to sessionStorage');
        }, 1000);
        
        return () => clearTimeout(timeoutId);
      }
    }
  }, [insuranceProvider, memberId, groupNumber, frontPreview, backPreview, tokenData, formId, isAddingNew, isEditing]);
  
  const handleFrontImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFrontImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFrontPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleBackImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBackImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setBackPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const validateForm = () => {
    if (!insuranceProvider.trim()) {
      setError('Please enter your insurance provider');
      return false;
    }
    
    if (!memberId.trim()) {
      setError('Please enter your member ID');
      return false;
    }
    
    if (isAddingNew && !frontImage && !frontPreview) {
      setError('Please upload a photo of the front of your insurance card');
      return false;
    }
    
    setError('');
    return true;
  };
  
  const handleSubmitPackage = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Create form data for file upload
      const formData = new FormData();
      formData.append('patient_id', tokenData.patient_id);
      formData.append('insurance_provider', insuranceProvider);
      formData.append('member_id', memberId);
      formData.append('group_number', groupNumber);
      
      if (frontImage) {
        formData.append('front_image', frontImage);
      }
      
      if (backImage) {
        formData.append('back_image', backImage);
      }
      
      if (isEditing && currentPackageId) {
        // Update existing package
        formData.append('insurance_id', currentPackageId);
        await patientApi.updateInsurancePackage(currentPackageId, formData, tokenData);
      } else {
        // Create new package
        await patientApi.submitInsurance(formData, tokenData);
      }
      
      // Refresh the list of packages
      await fetchInsurancePackages();
      
      // Reset form
      resetForm();
      
      // If this was the first package, mark as verified
      if (insurancePackages.length === 0) {
        setIsVerified(true);
        
        // Notify parent component after a short delay
        setTimeout(() => {
          onSuccess({
            insuranceProvider,
            memberId,
            groupNumber,
            hasInsuranceCard: true
          });
        }, 2000);
      }
    } catch (error) {
      console.error('Error submitting insurance information:', error);
      setError('Failed to submit insurance information. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleSetActiveStatus = async (packageId, isActive) => {
    try {
      setIsSubmitting(true);
      await patientApi.setInsurancePackageActive(packageId, isActive, tokenData);
      
      // Update the local state
      setInsurancePackages(prevPackages => 
        prevPackages.map(pkg => 
          pkg.id === packageId ? { ...pkg, is_active: isActive } : pkg
        )
      );
    } catch (error) {
      console.error('Error updating insurance package status:', error);
      setError('Failed to update insurance package status. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleEditPackage = (pkg) => {
    setCurrentPackageId(pkg.id);
    setInsuranceProvider(pkg.insurance_provider);
    setMemberId(pkg.member_id);
    setGroupNumber(pkg.group_number || '');
    
    // Set image previews if available
    if (pkg.front_image_path) {
      setFrontPreview(`/api/insurance/images/${pkg.front_image_path}`);
    }
    
    if (pkg.back_image_path) {
      setBackPreview(`/api/insurance/images/${pkg.back_image_path}`);
    }
    
    setIsEditing(true);
    setIsAddingNew(false);
  };
  
  const handleDeletePackage = async () => {
    if (!packageToDelete) return;
    
    try {
      setIsSubmitting(true);
      await patientApi.deleteInsurancePackage(packageToDelete, tokenData);
      
      // Update the local state
      setInsurancePackages(prevPackages => 
        prevPackages.filter(pkg => pkg.id !== packageToDelete)
      );
      
      // Close the confirmation dialog
      setShowDeleteConfirm(false);
      setPackageToDelete(null);
    } catch (error) {
      console.error('Error deleting insurance package:', error);
      setError('Failed to delete insurance package. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const confirmDeletePackage = (packageId) => {
    setPackageToDelete(packageId);
    setShowDeleteConfirm(true);
  };
  
  const resetForm = () => {
    setInsuranceProvider('');
    setMemberId('');
    setGroupNumber('');
    setFrontImage(null);
    setBackImage(null);
    setFrontPreview('');
    setBackPreview('');
    setCurrentPackageId(null);
    setIsEditing(false);
    setIsAddingNew(false);
    setError('');
  };
  
  const handleVerifyUnchanged = async () => {
    try {
      setIsSubmitting(true);
      
      // Call API to verify insurance as unchanged
      await patientApi.verifyInsuranceUnchanged(tokenData.sub, tokenData.case_id, tokenData);
      
      // Show success message
      setIsVerified(true);
      
      // Notify parent component after a short delay
      setTimeout(() => {
        onSuccess({
          hasInsuranceCard: insurancePackages.length > 0,
          verifiedUnchanged: true
        });
      }, 2000);
    } catch (error) {
      console.error('Error verifying insurance as unchanged:', error);
      setError('Failed to verify insurance. Please try again.');
      // Also set the main error message state for consistency
      let msg = 'Failed to verify insurance. Please try again.';
      if (error.response?.data?.message) {
        msg = error.response.data.message;
      } else if (error.message) {
        msg = error.message;
      }
      setErrorMessage(msg);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleSkipVerification = () => {
    setIsVerified(true);
    setTimeout(() => {
      onSuccess({
        hasInsuranceCard: false,
        skippedDueToError: true,
        errorDetails: {
          message: errorMessage, // Use the current errorMessage state
          timestamp: new Date().toISOString()
        }
      });
    }, 1000); // Short delay before calling onSuccess
  };

  const triggerFrontImageUpload = () => {
    frontInputRef.current.click();
  };
  
  const triggerBackImageUpload = () => {
    backInputRef.current.click();
  };
  
  // Render the insurance packages list
  const renderInsurancePackages = () => {
    if (insurancePackages.length === 0 && !isLoadingPackages && !errorMessage) {
      return (
        <div className="alert alert-info my-4">
          No insurance information found. Please add your insurance details.
        </div>
      );
    }
    
    if (insurancePackages.length > 0) {
      return (
        <div className="insurance-packages-list my-4">
          {insurancePackages.map((pkg) => (
            <div
              key={pkg.id}
              className={`card mb-3 ${pkg.source === 'Core Server' ? 'border-primary' : ''}`}
            >
              <div className="card-header d-flex justify-content-between align-items-center">
                <div>
                  {pkg.source === 'Core Server' && (
                    <span className="badge bg-primary me-2">From Provider</span>
                  )}
                  <strong>{pkg.insurance_provider}</strong>
                </div>
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`active-switch-${pkg.id}`}
                    checked={pkg.is_active}
                    onChange={(e) => handleSetActiveStatus(pkg.id, e.target.checked)}
                    disabled={isSubmitting}
                  />
                  <label className="form-check-label" htmlFor={`active-switch-${pkg.id}`}>
                    {pkg.is_active ? 'Active' : 'Inactive'}
                  </label>
                </div>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <p><strong>Member ID:</strong> {pkg.member_id}</p>
                    {pkg.group_number && (
                      <p><strong>Group Number:</strong> {pkg.group_number}</p>
                    )}
                    <p>
                      <strong>Verified:</strong> {pkg.is_verified ? 'Yes' : 'No'}
                      {pkg.verification_method && ` (${pkg.verification_method})`}
                    </p>
                  </div>
                  <div className="col-md-6">
                    {pkg.front_image_path && (
                      <div className="mb-2">
                        <img 
                          src={`/api/insurance/images/${pkg.front_image_path}`}
                          alt="Front of insurance card"
                          className="insurance-card-thumbnail"
                          style={{ maxWidth: '100px', maxHeight: '60px' }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="card-footer d-flex justify-content-end">
                <button
                  className="btn btn-sm btn-outline-primary me-2"
                  onClick={() => handleEditPackage(pkg)}
                  disabled={isSubmitting}
                >
                  Edit
                </button>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => confirmDeletePackage(pkg.id)}
                  disabled={isSubmitting}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
          
          {!isAddingNew && !isEditing && (
            <button
              className="btn btn-primary mt-3"
              onClick={() => setIsAddingNew(true)}
              disabled={isSubmitting}
            >
              Add New Insurance Package
            </button>
          )}
        </div>
      );
    }
  };
  
  // Render the insurance form
  const renderInsuranceForm = () => {
    return (
      <form onSubmit={handleSubmitPackage}>
        <div className="form-group mb-3">
          <label htmlFor="insuranceProvider" className="form-label">
            Insurance Provider <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            id="insuranceProvider"
            value={insuranceProvider}
            onChange={(e) => setInsuranceProvider(e.target.value)}
            placeholder="e.g., Blue Cross Blue Shield, Aetna, etc."
            required
          />
        </div>
        
        <div className="form-group mb-3">
          <label htmlFor="memberId" className="form-label">
            Member ID / Policy Number <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            id="memberId"
            value={memberId}
            onChange={(e) => setMemberId(e.target.value)}
            placeholder="Enter your member ID"
            required
          />
        </div>
        
        <div className="form-group mb-3">
          <label htmlFor="groupNumber" className="form-label">
            Group Number (if applicable)
          </label>
          <input
            type="text"
            className="form-control"
            id="groupNumber"
            value={groupNumber}
            onChange={(e) => setGroupNumber(e.target.value)}
            placeholder="Enter your group number"
          />
        </div>
        
        <div className="row mb-4">
          <div className="col-md-6 mb-3">
            <label className="form-label">
              Front of Insurance Card {isAddingNew && <span className="text-danger">*</span>}
            </label>
            <input
              type="file"
              ref={frontInputRef}
              className="d-none"
              accept="image/*"
              onChange={handleFrontImageChange}
            />
            
            <div 
              className="card p-2 text-center cursor-pointer"
              onClick={triggerFrontImageUpload}
              style={{ cursor: 'pointer' }}
            >
              {frontPreview ? (
                <img
                  src={frontPreview}
                  alt="Front of insurance card"
                  className="insurance-card-preview"
                  style={{ maxHeight: '200px', objectFit: 'contain' }}
                />
              ) : (
                <div className="p-4">
                  <i className="fas fa-camera fs-1 mb-2"></i>
                  <p>Click to upload front of card</p>
                </div>
              )}
            </div>
          </div>
          
          <div className="col-md-6 mb-3">
            <label className="form-label">
              Back of Insurance Card (Optional)
            </label>
            <input
              type="file"
              ref={backInputRef}
              className="d-none"
              accept="image/*"
              onChange={handleBackImageChange}
            />
            
            <div 
              className="card p-2 text-center"
              onClick={triggerBackImageUpload}
              style={{ cursor: 'pointer' }}
            >
              {backPreview ? (
                <img
                  src={backPreview}
                  alt="Back of insurance card"
                  className="insurance-card-preview"
                  style={{ maxHeight: '200px', objectFit: 'contain' }}
                />
              ) : (
                <div className="p-4">
                  <i className="fas fa-camera fs-1 mb-2"></i>
                  <p>Click to upload back of card</p>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="d-flex justify-content-between">
          {(isAddingNew || isEditing) && insurancePackages.length > 0 && (
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={resetForm}
              disabled={isSubmitting}
            >
              Cancel
            </button>
          )}
          
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {isEditing ? 'Updating...' : 'Submitting...'}
              </>
            ) : (
              isEditing ? 'Update Insurance Package' : 'Add Insurance Package'
            )}
          </button>
        </div>
      </form>
    );
  };
  
  // Render the delete confirmation dialog
  const renderDeleteConfirmation = () => {
    if (!showDeleteConfirm) return null;
    
    return (
      <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Confirm Delete</h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isSubmitting}
              ></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this insurance package? This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-danger" 
                onClick={handleDeletePackage}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show"></div>
      </div>
    );
  };
  
  // Render the initial verification question
  const renderVerificationQuestion = () => {
    if (hasInsuranceChanged !== null || insurancePackages.length === 0) return null;
    
    return (
      <div className="card mb-4">
        <div className="card-body">
          <h5 className="card-title">Insurance Verification</h5>
          <p>Has your insurance or benefits changed since your last visit?</p>
          <div className="d-flex justify-content-center gap-3 mt-4">
            <button 
              className="btn btn-outline-primary"
              onClick={() => { setHasInsuranceChanged(true); setIsAddingNew(true); }}
              disabled={isSubmitting}
            >
              Yes, it has changed
            </button>
            <button 
              className="btn btn-primary"
              onClick={handleVerifyUnchanged}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Verifying...
                </>
              ) : (
                'No, it\'s the same'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  return (
    <div className="insurance-verification-container">
      <div className="card">
        <div className="card-body">
          <h3 className="card-title mb-4">Insurance Information</h3>
          
          {!isVerified ? (
            <>
              <p className="mb-4">
                Please verify your insurance information to complete the check-in process.
              </p>
              
              {error && (
                <div className="alert alert-danger mb-4">
                  {error}
                </div>
              )}
              
              {/* Show loading spinner and messages */}
              {isLoadingPackages && (
                <div className="text-center my-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">
                    {/* Render loading message with skip link if applicable */}
                    {loadingMessages[loadingMessageIndex].includes('[SKIP_LINK]') ? (
                      <>
                        {loadingMessages[loadingMessageIndex].split('[SKIP_LINK]')[0]}
                        <p className='strong'><a href="#" onClick={(e) => { e.preventDefault(); handleSkipVerification(); }}>
                          Skip insurance verification
                        </a></p>
                        {loadingMessages[loadingMessageIndex].split('[SKIP_LINK]')[1]}
                      </>
                    ) : (
                      loadingMessages[loadingMessageIndex]
                    )}
                  </p>
                </div>
              )}
              
              {/* Show error message */}
              {errorMessage && !isLoadingPackages && (
                <div className="alert alert-warning mb-4">
                  <p className="card-title">Whoa! We sailed right into choppy waters</p><p>{errorMessage}</p>
                    <button
                      className="btn btn-outline-secondary me-2"
                      onClick={fetchInsurancePackages}
                    >
                      Try Again
                    </button>
                  <p className="mt-2">Any applicable copays/coinsurance will still apply. <a href="https://ankr.us/telehealth-consent" target="_blank" rel="noopener noreferrer">Click here for details.</a></p>
                </div>
              )}
              
              {/* Initial verification question */}
              {!isLoadingPackages && !errorMessage && hasInsuranceChanged === null && renderVerificationQuestion()}
              
              {/* Show packages list if user has indicated insurance changed or there are no packages */}
              {!isLoadingPackages && !errorMessage && (hasInsuranceChanged === true || insurancePackages.length === 0) && (
                <>
                  {renderInsurancePackages()}
                  
                  {/* Form to add/edit insurance package */}
                  {(isAddingNew || isEditing) && renderInsuranceForm()}
                </>
              )}
              
              {/* Continue button */}
              {!isLoadingPackages && !errorMessage && insurancePackages.length > 0 && !isAddingNew && !isEditing && hasInsuranceChanged === true && (
                <button
                  className="btn btn-primary w-100 mt-3"
                  onClick={() => {
                    setIsVerified(true);
                    setTimeout(() => {
                      onSuccess({
                        hasInsuranceCard: true,
                        insurancePackages: insurancePackages
                      });
                    }, 2000);
                  }}
                  disabled={isSubmitting}
                >
                  Continue with Selected Insurance
                </button>
              )}
            </>
          ) : (
            <div className="text-center">
              <div className="success-message mb-3">
                <i className="fas fa-check-circle me-2"></i>
                Insurance information verified successfully!
              </div>
              <div>Proceeding to the next step...</div>
            </div>
          )}
        </div>
      </div>
      
      {/* Delete confirmation dialog */}
      {renderDeleteConfirmation()}
    </div>
  );
};

export default InsuranceVerification;